import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth }   from '../context/AuthContext';
import { useEquipo } from '../context/EquipoContext';
import { crearDesafio, getDesafiosEquipo, actualizarEstadoDesafio, getEquipos, crearCobroDesafio } from '../services/api';
import PropuestaDesafio from '../components/desafio/PropuestaDesafio';
import ServiciosDesafio from '../components/desafio/ServiciosDesafio';
import DepositoFirme    from '../components/desafio/DepositoFirme';

const ETAPAS = [
  { num: 1, label: 'Propuesta' },
  { num: 2, label: 'Servicios' },
  { num: 3, label: 'Depósito'  },
];

const RAZONES_CANCELACION = [
  'No presentación del rival',
  'Recinto cerrado',
  'Cancha ocupada',
  'Inclemencia del tiempo',
  'Sin compañero disponible',
  'Fuerza mayor',
  'Otro motivo',
];

function getIniciales(nombre) {
  if (!nombre) return '??';
  return nombre.split(' ').filter(p => p.length > 0).slice(0,2).map(p => p[0].toUpperCase()).join('');
}

function formatFecha(fechaStr) {
  if (!fechaStr) return '';
  try {
    const solo = String(fechaStr).slice(0, 10);
    const [y, m, d] = solo.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return fechaStr; }
}

function abrirMapa(direccion, ciudad, nombre) {
  if (!direccion) return;
  const partes = [nombre, direccion, ciudad].filter(Boolean).join(', ');
  const query  = encodeURIComponent(partes);
  window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
}

function estadoPartido(fecha, hora) {
  if (!fecha || !hora) return 'futuro';
  const solo = String(fecha).slice(0, 10);
  const ahoraChile   = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Santiago' }));
  const partidoChile = new Date(new Date(`${solo}T${hora}:00`).toLocaleString('en-US', { timeZone: 'America/Santiago' }));
  const diffHoras = (partidoChile - ahoraChile) / 3600000;
  if (diffHoras < 0)  return 'jugado';
  if (diffHoras < 4)  return 'menos4hrs';
  return 'futuro';
}

// Helper: línea de info del partido según deporte
function labelModalidad(modalidad) {
  const labels = {
    pierde_paga: '🏆 Pierde Paga',
    pago_medias: '🤝 Pago a Medias',
    invitacion:  '🎁 Invitación',
  };
  return labels[modalidad] || '🤝 Pago a Medias';
}

function InfoPartidoCard({ d }) {
  const esFutbol = !d.deporte || d.deporte === 'futbol';

  if (esFutbol) {
    return (
      <>
        <div style={{ fontSize: 11, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 }}>
          💳 {labelModalidad(d.modalidad_pago)}
        </div>
        <div style={{ fontSize: 11, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 }}>
          {d.con_arbitro
            ? <span style={{ color: '#F59E0B' }}>🟡 Con árbitro</span>
            : <span style={{ color: '#9BA5B7' }}>Sin árbitro</span>
          }
        </div>
      </>
    );
  }

  const deporteLabel = d.deporte === 'padel' ? '🎾 Pádel' : '🎾 Tenis';
  const juegoLabel   = d.modalidad_juego === 'dobles' ? 'Dobles' : 'Individual';
  const formatoLabels = {
    '2sets_supertb':        '2 Sets + Súper TB',
    '2sets_puntoro':        '2 Sets + Punto de Oro',
    '2sets_puntoro_supertb':'2 Sets + Punto de Oro + Súper TB',
  };
  const formatoLabel = formatoLabels[d.formato_sets] || '2 Sets + Súper TB';
  const tipoLabel    = d.tipo_partido === 'amistoso' ? '🤝 Amistoso' : '🏅 Oficial';

  return (
    <>
      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 }}>
        💳 {labelModalidad(d.modalidad_pago)}
      </div>
      <div style={{ fontSize: 11, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 }}>
        <span style={{ color: '#16A34A', fontWeight: 700 }}>
          {deporteLabel} · {juegoLabel} · {formatoLabel} · {tipoLabel}
        </span>
      </div>
    </>
  );
}

export default function Desafios() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { token }  = useAuth();
  const { equipo } = useEquipo();

  const rivalInicial = location.state?.rival || null;

  const [etapa,     setEtapa]     = useState(1);
  const [desafio,   setDesafio]   = useState({ rival: rivalInicial });
  const [servicios, setServicios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');
  const [desafioEnviado, setDesafioEnviado] = useState(null);

  const [desafios, setDesafios] = useState([]);
  const [equipos,  setEquipos]  = useState([]);
  const [cargando, setCargando] = useState(false);
  const [pagando,  setPagando]  = useState(null); // desafio.id en proceso de pago

  const iniciarPago = async (desafioId) => {
    setPagando(desafioId);
    try {
      const data = await crearCobroDesafio(desafioId, token);
      if (data?.payment_url) {
        window.location.href = data.payment_url;
      } else {
        alert('No se pudo iniciar el pago. Intenta nuevamente.');
        setPagando(null);
      }
    } catch (e) {
      alert('Error al conectar con la pasarela de pago.');
      setPagando(null);
    }
  };

  const [modalCancelar,      setModalCancelar]      = useState(null);
  const [modalCompanero,     setModalCompanero]     = useState(null);
  const [busqCompVisitante,  setBusqCompVisitante]  = useState('');
  const [compVisitante,      setCompVisitante]      = useState(null);
  const [resBusqComp,        setResBusqComp]        = useState([]);
  const [buscandoComp,       setBuscandoComp]       = useState(false);
  const [guardandoComp,      setGuardandoComp]      = useState(false);
  const [razonCancelacion,   setRazonCancelacion]   = useState('');
  const [detalleCancelacion, setDetalleCancelacion] = useState('');
  const [cancelando,         setCancelando]         = useState(false);

  const modoLista = !rivalInicial && !desafioEnviado;

  useEffect(() => {
    if (!equipo?.id || !token) return;
    const cargar = () => {
      Promise.all([
        getDesafiosEquipo(equipo.id, token),
        getEquipos(),
      ]).then(([desafiosData, equiposData]) => {
        if (Array.isArray(desafiosData)) setDesafios(desafiosData);
        if (Array.isArray(equiposData))  setEquipos(equiposData);
      }).catch(() => {})
      .finally(() => setCargando(false));
    };
    setCargando(true);
    cargar();
    const intervalo = setInterval(cargar, 30000);
    return () => clearInterval(intervalo);
  }, [equipo, token]);

  const getNombreEquipo = (id) => {
    const e = equipos.find(eq => eq.id === id);
    return e?.nombre || 'Equipo #' + id;
  };

  const getColoresEquipo = (id) => {
    const pares = [
      ['#EF4444','#B91C1C'], ['#1A6BFF','#0F4FCC'],
      ['#16A34A','#166534'], ['#7C3AED','#5B21B6'],
      ['#FF6B1A','#CC5214'], ['#F59E0B','#D97706'],
    ];
    return pares[(id || 0) % pares.length];
  };

  const desafiosRecibidos   = desafios.filter(d =>
    d.equipo_visitante_id === equipo?.id && d.estado === 'pendiente'
  );
  const desafiosEnviados    = desafios.filter(d =>
    d.equipo_local_id === equipo?.id &&
    (d.estado === 'pendiente' || d.estado === 'rechazado')
  );
  const desafiosAceptados   = desafios.filter(d =>
    (d.equipo_local_id === equipo?.id || d.equipo_visitante_id === equipo?.id) &&
    d.estado === 'aceptado' &&
    d.con_arbitro === true &&
    !d.arbitro_id
  );
  const desafiosConfirmados = desafios.filter(d =>
    (d.equipo_local_id === equipo?.id || d.equipo_visitante_id === equipo?.id) &&
    (d.estado === 'confirmado' || (d.estado === 'aceptado' && !d.con_arbitro))
  );

  const handleAceptar = async (id) => {
    const desafio = desafios.find(d => d.id === id);
    if (desafio?.modalidad_juego === 'dobles') {
      setModalCompanero(desafio);
      setCompVisitante(null);
      setBusqCompVisitante('');
      return;
    }
    await actualizarEstadoDesafio(id, 'aceptado', token);
    setDesafios(prev => prev.map(d => d.id === id ? { ...d, estado: 'aceptado' } : d));
  };

  const handleRechazar = async (id) => {
    await actualizarEstadoDesafio(id, 'rechazado', token);
    setDesafios(prev => prev.filter(d => d.id !== id));
  };

  const handleCancelarEnviado = async (id) => {
    if (!window.confirm('¿Cancelar este desafío? Se notificará al rival.')) return;
    await actualizarEstadoDesafio(id, 'cancelado', token);
    setDesafios(prev => prev.filter(d => d.id !== id));
  };

  const buscarCompVisitante = async (query) => {
    setBusqCompVisitante(query);
    if (query.length < 2) { setResBusqComp([]); return; }
    setBuscandoComp(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios?q=${encodeURIComponent(query)}`);
      const data = await res.json();
      setResBusqComp(Array.isArray(data) ? data.slice(0, 5) : []);
    } catch { setResBusqComp([]); }
    finally { setBuscandoComp(false); }
  };

  const handleAceptarDobles = async () => {
    if (!modalCompanero) return;
    setGuardandoComp(true);
    try {
      await actualizarEstadoDesafio(modalCompanero.id, 'aceptado', token);
      if (compVisitante?.id) {
        await fetch(`${process.env.REACT_APP_API_URL}/api/desafios/${modalCompanero.id}/estado`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ companero_visitante_id: compVisitante.id }),
        });
      }
      setDesafios(prev => prev.map(d => d.id === modalCompanero.id ? { ...d, estado: 'aceptado' } : d));
      setModalCompanero(null);
      setCompVisitante(null);
      setBusqCompVisitante('');
    } catch { alert('Error al aceptar. Intenta de nuevo.'); }
    finally { setGuardandoComp(false); }
  };

  const abrirModalCancelar = (desafio) => {
    setModalCancelar(desafio);
    setRazonCancelacion('');
    setDetalleCancelacion('');
  };

  const handleCancelarPartido = async () => {
    if (!razonCancelacion) return;
    setCancelando(true);
    try {
      const API = process.env.REACT_APP_API_URL;
      await fetch(`${API}/api/desafios/${modalCancelar.id}/estado`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          estado:              'cancelado',
          razon_cancelacion:   razonCancelacion,
          detalle_cancelacion: detalleCancelacion,
        }),
      });
      setDesafios(prev => prev.filter(d => d.id !== modalCancelar.id));
      setModalCancelar(null);
    } catch {
      alert('Error al cancelar. Intenta de nuevo.');
    } finally {
      setCancelando(false);
    }
  };

  const handlePropuesta = async (datos) => {
    setGuardando(true); setErrorForm('');
    try {
      const data = await crearDesafio({
        equipo_local_id:     equipo?.id                    || null,
        equipo_visitante_id: datos.rival?.id               || null,
        recinto_id:          datos.recinto?.id             || null,
        cancha_id:           datos.cancha?.id              || null,
        fecha:               datos.fecha                   || null,
        hora:                datos.hora                    || null,
        duracion:            datos.duracion                || 1,
        con_arbitro:         datos.conArbitro              || false,
        cancha_externa:      datos.canchaExterna           || false,
        recinto_manual:      datos.nombreRecinto           || null,
        cancha_manual:       datos.nombreCancha            || null,
        direccion_manual:    datos.direccionManual         || null,
        deposito:            datos.deposito                || 0,
        mensaje:             datos.mensaje                 || '',
        modalidad_pago:      datos.modalidad_pago          || 'pago_medias',
        modalidad_juego:     datos.modalidad_juego         || 'individual',
        companero_local_id:  datos.companero_local_id      || null,
        formato_sets:        datos.formato_sets            || null,
        tipo_partido:        datos.tipo_partido            || null,
      }, token);
      if (data.error) { setErrorForm(data.error); return; }

      // Si hay compañero externo, enviar invitación por WSP
      if (datos.companero_externo?.nombre && datos.companero_externo?.celular) {
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/api/invitaciones`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              desafio_id: data.id,
              tipo:       'companero_local',
              nombre:     datos.companero_externo.nombre,
              celular:    datos.companero_externo.celular,
            }),
          });
        } catch (e) { console.error('Error enviando invitación compañero:', e); }
      }

      setDesafio({ ...datos, id: data.id });
      setDesafioEnviado(data);
    } catch { setErrorForm('Error de conexión'); }
    finally  { setGuardando(false); }
  };

  const handleServicios  = (sel) => { setServicios(sel); setEtapa(2); };
  const handleDepositado = () => { navigate('/app/dashboard'); };

  if (desafioEnviado && etapa === 1) {
    return (
      <div style={styles.exitoPagina}>
        <div style={styles.exitoIcono}>⚡</div>
        <div style={styles.exitoTitulo}>¡Desafío enviado!</div>
        <div style={styles.exitoSub}>
          vs {desafio.rival?.nombre} · {desafio.fecha} · {desafio.hora}
        </div>
        <div style={styles.exitoNota}>
          {desafio.rival?.nombre} tiene 48 horas para aceptar o rechazar.
          Te notificaremos cuando responda.
        </div>
        <button style={styles.btnVolver} onClick={() => { setDesafioEnviado(null); setDesafio({ rival: null }); setEtapa(1); }}>
          Ver mis desafíos →
        </button>
      </div>
    );
  }

  if (!modoLista && etapa > 1) {
    return (
      <div style={styles.paginaFlujo}>
        <div style={styles.container}>
          <div style={styles.stepper}>
            {ETAPAS.map(({ num, label }, i) => {
              const completado = etapa > num;
              const activo     = etapa === num;
              return (
                <div key={num} style={styles.stepItem}>
                  <div style={{
                    ...styles.stepCirculo,
                    background: completado ? '#16A34A' : activo ? '#FF6B1A' : '#E8ECF4',
                  }}>
                    <span style={{ color: completado || activo ? 'white' : '#9BA5B7', fontSize: 12, fontWeight: 700 }}>
                      {completado ? '✓' : num}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, fontWeight: 600,
                    color: activo ? '#141922' : '#9BA5B7',
                    fontFamily: "'Barlow', sans-serif",
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {label}
                  </span>
                  {i < ETAPAS.length - 1 && (
                    <div style={{ ...styles.stepLinea, background: completado ? '#16A34A' : '#E8ECF4' }} />
                  )}
                </div>
              );
            })}
          </div>
          {errorForm && <div style={styles.errorBanner}>{errorForm}</div>}
          <div style={styles.cardFlujo}>
            {etapa === 2 && (
              <ServiciosDesafio
                desafio={desafio}
                onConfirmar={handleServicios}
                onSaltarse={() => { setServicios([]); setEtapa(3); }}
              />
            )}
            {etapa === 3 && (
              <DepositoFirme
                desafio={desafio}
                servicios={servicios}
                onPagar={handleDepositado}
                onVolver={() => setEtapa(2)}
                guardando={guardando}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (rivalInicial) {
    return (
      <div style={styles.paginaFlujo}>
        <div style={styles.container}>
          {errorForm && <div style={styles.errorBanner}>{errorForm}</div>}
          <div style={styles.cardFlujo}>
            <PropuestaDesafio
              rival={rivalInicial}
              onEnviar={handlePropuesta}
              onCancelar={() => navigate('/app/matchmaking')}
              guardando={guardando}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pagina}>

      <div style={styles.header}>
        <div style={styles.headerInner}>
          <div>
            <div style={styles.titulo}>Desafíos</div>
            <div style={styles.sub}>
              {desafiosRecibidos.length > 0
                ? `${desafiosRecibidos.length} pendientes de tu respuesta`
                : 'Gestiona tus desafíos deportivos'}
            </div>
          </div>
          <button style={styles.btnNuevo} onClick={() => navigate('/app/matchmaking')}>
            ⚡ Nuevo desafío
          </button>
        </div>
      </div>

      {cargando ? (
        <div style={styles.cargando}>Cargando desafíos...</div>
      ) : (
        <div style={styles.lienzo}>

          {/* ── RECIBIDOS ── */}
          <div style={styles.seccion}>
            <div style={styles.seccionHeader}>
              <div style={{ ...styles.seccionIcono, background: 'rgba(255,107,26,0.15)' }}>⚡</div>
              <div>
                <div style={{ ...styles.seccionTitulo, color: '#FF6B1A' }}>Recibidos</div>
                <div style={styles.seccionSub}>Pendientes de tu respuesta</div>
              </div>
              <div style={{ ...styles.seccionBadge, background: '#FF6B1A' }}>
                {desafiosRecibidos.length}
              </div>
            </div>
            {desafiosRecibidos.length === 0 ? (
              <div style={styles.seccionVacio}>📭 Sin desafíos recibidos</div>
            ) : (
              desafiosRecibidos.map(d => {
                const [c1, c2] = getColoresEquipo(d.equipo_local_id);
                const nombre   = getNombreEquipo(d.equipo_local_id);
                return (
                  <div key={d.id} style={{ ...styles.card, borderLeft: '4px solid #FF6B1A' }}>
                    <div style={styles.cardInner}>
                      <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                        {getIniciales(nombre)}
                      </div>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardRival}>{nombre}</div>
                        {d.fecha && <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}{d.duracion ? ` · ${Math.round(d.duracion * 60)} min` : ''}</div>}
                        {(d.recinto_nombre || d.recinto_manual) && (
                          <div style={styles.cardFecha}>
                            🏟️ {d.recinto_manual || d.recinto_nombre}
                            {(d.recinto_direccion || d.direccion_manual) && (
                              <span style={styles.linkMapa} onClick={() => abrirMapa(d.direccion_manual || d.recinto_direccion, d.recinto_ciudad, d.recinto_manual || d.recinto_nombre)}>
                                📍 Ver mapa
                              </span>
                            )}
                          </div>
                        )}
                        {d.cancha_manual && <div style={styles.cardFecha}>⛳ {d.cancha_manual}</div>}
                        <InfoPartidoCard d={d} />
                        {d.mensaje && <div style={styles.cardMensaje}>"{d.mensaje}"</div>}
                      </div>
                      <div style={{ ...styles.estadoBadge, background: 'rgba(255,107,26,0.1)', color: '#FF6B1A' }}>
                        ⏳ Pendiente
                      </div>
                    </div>
                    <div style={styles.cardBotones}>
                      <button style={styles.btnRechazar} onClick={() => handleRechazar(d.id)}>
                        Rechazar
                      </button>
                      <button style={styles.btnAceptar} onClick={() => handleAceptar(d.id)}>
                        ⚡ Aceptar desafío
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── ENVIADOS ── */}
          <div style={styles.seccion}>
            <div style={styles.seccionHeader}>
              <div style={{ ...styles.seccionIcono, background: 'rgba(26,107,255,0.15)' }}>🚀</div>
              <div>
                <div style={{ ...styles.seccionTitulo, color: '#1A6BFF' }}>Enviados</div>
                <div style={styles.seccionSub}>Esperando respuesta del rival</div>
              </div>
              <div style={{ ...styles.seccionBadge, background: '#1A6BFF' }}>
                {desafiosEnviados.length}
              </div>
            </div>
            {desafiosEnviados.length === 0 ? (
              <div style={styles.seccionVacio}>
                🏃 Sin desafíos enviados
                <button style={styles.btnBuscarPequeno} onClick={() => navigate('/app/matchmaking')}>
                  Buscar rival →
                </button>
              </div>
            ) : (
              desafiosEnviados.map(d => {
                const [c1, c2] = getColoresEquipo(d.equipo_visitante_id);
                const nombre   = getNombreEquipo(d.equipo_visitante_id);
                return (
                  <div key={d.id} style={{ ...styles.card, borderLeft: '4px solid #1A6BFF' }}>
                    <div style={styles.cardInner}>
                      <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                        {getIniciales(nombre)}
                      </div>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardRival}>{nombre}</div>
                        {d.fecha && <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}{d.duracion ? ` · ${Math.round(d.duracion * 60)} min` : ''}</div>}
                        <InfoPartidoCard d={d} />
                        {d.mensaje && <div style={styles.cardMensaje}>"{d.mensaje}"</div>}
                      </div>
                      <div style={{
                        ...styles.estadoBadge,
                        background: d.estado === 'rechazado' ? 'rgba(239,68,68,0.1)' : 'rgba(26,107,255,0.1)',
                        color: d.estado === 'rechazado' ? '#EF4444' : '#1A6BFF',
                      }}>
                        {d.estado === 'rechazado' ? '✗ Rechazado' : '⏳ Esperando'}
                      </div>
                    </div>
                    <div style={styles.cancelarWrap}>
                      <button style={styles.btnCancelarDesafio} onClick={() => handleCancelarEnviado(d.id)}>
                        Cancelar desafío
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* ── ACEPTADOS — buscando árbitro ── */}
          {desafiosAceptados.length > 0 && (
            <div style={styles.seccion}>
              <div style={styles.seccionHeader}>
                <div style={{ ...styles.seccionIcono, background: 'rgba(245,158,11,0.15)' }}>🔍</div>
                <div>
                  <div style={{ ...styles.seccionTitulo, color: '#F59E0B' }}>Buscando árbitro</div>
                  <div style={styles.seccionSub}>Rival aceptó · árbitro pendiente</div>
                </div>
                <div style={{ ...styles.seccionBadge, background: '#F59E0B' }}>
                  {desafiosAceptados.length}
                </div>
              </div>
              {desafiosAceptados.map(d => {
                const esLocal  = d.equipo_local_id === equipo?.id;
                const rivalId  = esLocal ? d.equipo_visitante_id : d.equipo_local_id;
                const [c1, c2] = getColoresEquipo(rivalId);
                const nombre   = getNombreEquipo(rivalId);
                return (
                  <div key={d.id} style={{ ...styles.card, borderLeft: '4px solid #F59E0B' }}>
                    <div style={styles.cardInner}>
                      <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                        {getIniciales(nombre)}
                      </div>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardRival}>vs {nombre}</div>
                        {d.fecha && <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}{d.duracion ? ` · ${Math.round(d.duracion * 60)} min` : ''}</div>}
                        {(d.recinto_nombre || d.recinto_manual) && (
                          <div style={styles.cardFecha}>
                            🏟️ {d.recinto_manual || d.recinto_nombre}
                          </div>
                        )}
                        {d.cancha_manual && <div style={styles.cardFecha}>⛳ {d.cancha_manual}</div>}
                        <InfoPartidoCard d={d} />
                      </div>
                      <div style={{ ...styles.estadoBadge, background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}>
                        🔍 Sin árbitro
                      </div>
                    </div>
                    {esLocal && !d.con_arbitro && (
                      <div style={styles.cancelarWrap}>
                        <button
                          style={{ ...styles.btnAceptar, background: '#16A34A' }}
                          onClick={() => { setDesafio(d); setDesafioEnviado(d); setEtapa(2); }}
                        >
                          Continuar → Servicios
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── CONFIRMADOS ── */}
          <div style={styles.seccion}>
            <div style={styles.seccionHeader}>
              <div style={{ ...styles.seccionIcono, background: 'rgba(22,163,74,0.15)' }}>✅</div>
              <div>
                <div style={{ ...styles.seccionTitulo, color: '#16A34A' }}>Confirmados</div>
                <div style={styles.seccionSub}>Partido completo · a jugar</div>
              </div>
              <div style={{ ...styles.seccionBadge, background: '#16A34A' }}>
                {desafiosConfirmados.length}
              </div>
            </div>
            {desafiosConfirmados.length === 0 ? (
              <div style={styles.seccionVacio}>✓ Sin partidos confirmados aún</div>
            ) : (
              desafiosConfirmados.map(d => {
                const esLocal  = d.equipo_local_id === equipo?.id;
                const rivalId  = esLocal ? d.equipo_visitante_id : d.equipo_local_id;
                const [c1, c2] = getColoresEquipo(rivalId);
                const nombre   = getNombreEquipo(rivalId);
                const ep       = estadoPartido(d.fecha, d.hora);
                return (
                  <div key={d.id} style={{ ...styles.card, borderLeft: '4px solid #16A34A' }}>
                    <div style={styles.cardInner}>
                      <div style={{ ...styles.crest, background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                        {getIniciales(nombre)}
                      </div>
                      <div style={styles.cardInfo}>
                        <div style={styles.cardRival}>vs {nombre}</div>
                        {d.fecha && <div style={styles.cardFecha}>📅 {formatFecha(d.fecha)} · {d.hora}{d.duracion ? ` · ${Math.round(d.duracion * 60)} min` : ''}</div>}
                        {(d.recinto_nombre || d.recinto_manual) && (
                          <div style={styles.cardFecha}>
                            🏟️ {d.recinto_manual || d.recinto_nombre}
                            {(d.recinto_direccion || d.direccion_manual) && (
                              <span style={styles.linkMapa} onClick={() => abrirMapa(d.direccion_manual || d.recinto_direccion, d.recinto_ciudad, d.recinto_manual || d.recinto_nombre)}>
                                📍 Ver mapa
                              </span>
                            )}
                          </div>
                        )}
                        {d.cancha_manual && <div style={styles.cardFecha}>⛳ {d.cancha_manual}</div>}
                        <InfoPartidoCard d={d} />
                      </div>
                      <div style={{ ...styles.estadoBadge, background: 'rgba(22,163,74,0.1)', color: '#16A34A' }}>
                        ✅ Completo
                      </div>
                    </div>
                    {esLocal && d.pago_estado && d.pago_estado !== 'pagado' && d.pago_estado !== 'sin_reembolso' && (
                      <div style={{
                        margin: '0 16px 12px', padding: '10px 12px',
                        background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: 10,
                      }}>
                        <div style={{ fontSize: 12, color: '#9A3412', fontFamily: "'Barlow', sans-serif", marginBottom: 8 }}>
                          💳 Pago del desafío pendiente {d.pago_monto ? `— $${Number(d.pago_monto).toLocaleString('es-CL')}` : ''}
                        </div>
                        <button
                          style={{ ...styles.btnAceptar, background: '#FF6B1A', width: '100%', opacity: pagando === d.id ? 0.6 : 1 }}
                          onClick={() => iniciarPago(d.id)}
                          disabled={pagando === d.id}
                        >
                          {pagando === d.id ? 'Conectando...' : 'Pagar con Khipu →'}
                        </button>
                      </div>
                    )}
                    {esLocal && d.pago_estado === 'pagado' && (
                      <div style={{ margin: '0 16px 12px' }}>
                        <div style={{ ...styles.estadoBadge, background: 'rgba(22,163,74,0.1)', color: '#16A34A', display: 'inline-block' }}>
                          💰 Pagado
                        </div>
                      </div>
                    )}
                    <div style={{ ...styles.cancelarWrap, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      {esLocal && (
                        <button
                          style={{ ...styles.btnAceptar, background: '#16A34A' }}
                          onClick={() => { setDesafio(d); setDesafioEnviado(d); setEtapa(2); }}
                        >
                          Continuar → Servicios
                        </button>
                      )}
                      {ep === 'jugado' ? null : ep === 'menos4hrs' ? (
                        <button
                          style={{ ...styles.btnCancelarDesafio, opacity: 0.7 }}
                          onClick={() => abrirModalCancelar(d)}
                          title="Cancelación tardía — puede afectar tu reputación"
                        >
                          ✕ Cancelar (penalización)
                        </button>
                      ) : (
                        <button
                          style={styles.btnCancelarDesafio}
                          onClick={() => abrirModalCancelar(d)}
                        >
                          ✕ Cancelar partido
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      )}

      {/* Modal cancelación */}
      {modalCancelar && (
        <div style={styles.modalOverlay} onClick={() => setModalCancelar(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitulo}>✕ Cancelar partido</div>
              <button style={styles.btnCerrar} onClick={() => setModalCancelar(null)}>✕</button>
            </div>
            <div style={styles.modalSub}>
              vs {getNombreEquipo(modalCancelar.equipo_local_id === equipo?.id
                ? modalCancelar.equipo_visitante_id
                : modalCancelar.equipo_local_id)} · {formatFecha(modalCancelar.fecha)}
            </div>

            {estadoPartido(modalCancelar.fecha, modalCancelar.hora) === 'menos4hrs' && (
              <div style={{
                background: '#FEF2F2', border: '1.5px solid #FCA5A5',
                borderRadius: 12, padding: '12px 16px', marginBottom: 16,
                fontSize: 12, color: '#EF4444', fontFamily: "'Barlow', sans-serif",
                lineHeight: 1.5,
              }}>
                ⚠️ <strong>Cancelación tardía</strong> — Estás cancelando con menos de 4 horas de anticipación. Esto puede afectar tu reputación en la plataforma.
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <div style={styles.modalLabel}>Razón de cancelación</div>
              {RAZONES_CANCELACION.map(r => (
                <div
                  key={r}
                  style={{
                    padding: '10px 14px', borderRadius: 10, marginBottom: 6,
                    cursor: 'pointer', fontFamily: "'Barlow', sans-serif", fontSize: 13,
                    background: razonCancelacion === r ? '#FEF2F2' : '#F4F6FA',
                    border: razonCancelacion === r ? '1.5px solid #FCA5A5' : '1.5px solid transparent',
                    color: razonCancelacion === r ? '#EF4444' : '#4A5568',
                    fontWeight: razonCancelacion === r ? 700 : 400,
                  }}
                  onClick={() => setRazonCancelacion(r)}
                >
                  {razonCancelacion === r ? '● ' : '○ '}{r}
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={styles.modalLabel}>Detalle adicional (opcional)</div>
              <textarea
                style={{
                  width: '100%', padding: '10px 14px',
                  border: '1.5px solid #E8ECF4', borderRadius: 10,
                  fontSize: 13, fontFamily: "'Barlow', sans-serif",
                  resize: 'none', outline: 'none', boxSizing: 'border-box',
                  color: '#141922',
                }}
                rows={3}
                placeholder="Explica brevemente el motivo..."
                value={detalleCancelacion}
                onChange={e => setDetalleCancelacion(e.target.value)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <button style={styles.btnCancelarModal} onClick={() => setModalCancelar(null)}>
                Volver
              </button>
              <button
                style={{
                  ...styles.btnConfirmarCancelar,
                  opacity: !razonCancelacion || cancelando ? 0.4 : 1,
                  cursor: !razonCancelacion || cancelando ? 'not-allowed' : 'pointer',
                }}
                onClick={handleCancelarPartido}
                disabled={!razonCancelacion || cancelando}
              >
                {cancelando ? 'Cancelando...' : '✕ Confirmar cancelación'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  pagina: { minHeight: '100vh', background: '#F4F6FA' },
  header: {
    background: '#0D1117', padding: '20px 32px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  headerInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  titulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 30, fontWeight: 800, color: 'white', marginBottom: 4,
  },
  sub: { fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  btnNuevo: {
    background: '#FF6B1A', color: 'white', border: 'none',
    borderRadius: 10, padding: '10px 18px', cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 700,
  },
  cargando: {
    padding: '60px', textAlign: 'center',
    color: '#9BA5B7', fontSize: 13, fontFamily: "'Barlow', sans-serif",
  },
  lienzo: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 20, padding: '24px 32px', alignItems: 'start',
  },
  seccion: {
    background: 'white', border: '1px solid #E8ECF4',
    borderRadius: 16, overflow: 'hidden',
  },
  seccionHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '16px 16px 14px', borderBottom: '1px solid #F4F6FA',
  },
  seccionIcono: {
    width: 36, height: 36, borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, flexShrink: 0,
  },
  seccionTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 16, fontWeight: 800, marginBottom: 1,
  },
  seccionSub: { fontSize: 11, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif" },
  seccionBadge: {
    marginLeft: 'auto', color: 'white', fontSize: 12, fontWeight: 700,
    padding: '3px 10px', borderRadius: 20,
    fontFamily: "'Barlow', sans-serif", flexShrink: 0,
  },
  seccionVacio: {
    padding: '24px 16px', textAlign: 'center',
    fontSize: 12, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif",
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
  },
  btnBuscarPequeno: {
    background: '#FF6B1A', color: 'white', border: 'none',
    borderRadius: 8, padding: '7px 14px', cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700,
  },
  card: { borderBottom: '1px solid #F4F6FA', padding: '14px 16px', background: 'white' },
  cardInner: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  crest: {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 800, color: 'white',
  },
  cardInfo: { flex: 1, minWidth: 0 },
  cardRival: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 15, fontWeight: 700, color: '#141922', marginBottom: 2,
  },
  cardFecha: { fontSize: 11, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 2 },
  cardMensaje: {
    fontSize: 10, color: '#9BA5B7', fontFamily: "'Barlow', sans-serif",
    fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  estadoBadge: {
    fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
    fontFamily: "'Barlow', sans-serif", whiteSpace: 'nowrap', flexShrink: 0,
  },
  linkMapa: {
    color: '#1A6BFF', cursor: 'pointer', fontWeight: 700,
    fontSize: 11, marginLeft: 6,
  },
  cardBotones: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 6, marginTop: 10 },
  btnRechazar: {
    padding: '8px', background: 'white', border: '1.5px solid #E8ECF4',
    borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#6B7280',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
  },
  btnAceptar: {
    padding: '8px', background: '#FF6B1A', border: 'none', borderRadius: 8,
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 12, fontWeight: 800, color: 'white', cursor: 'pointer',
  },
  cancelarWrap: { marginTop: 10, textAlign: 'right' },
  btnCancelarDesafio: {
    background: 'transparent', border: '1.5px solid #FCA5A5',
    borderRadius: 8, padding: '6px 12px', color: '#EF4444',
    fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Barlow', sans-serif",
  },
  paginaFlujo: { minHeight: '100vh', background: '#F4F6FA', padding: '32px 20px' },
  container: { maxWidth: 640, margin: '0 auto' },
  stepper: { display: 'flex', alignItems: 'flex-start', justifyContent: 'center', marginBottom: 24 },
  stepItem: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    position: 'relative', flex: 1,
  },
  stepCirculo: {
    width: 34, height: 34, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
  },
  stepLinea: { position: 'absolute', top: 17, left: '50%', width: '100%', height: 2, zIndex: 0 },
  cardFlujo: {
    background: 'white', border: '1px solid #E8ECF4',
    borderRadius: 20, padding: '32px',
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.1)', border: '1px solid #FCA5A5',
    borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#EF4444',
    fontFamily: "'Barlow', sans-serif", marginBottom: 16,
  },
  exitoPagina: {
    minHeight: '100vh', background: '#0D1117',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', padding: '40px',
  },
  exitoIcono:  { fontSize: 64, marginBottom: 16 },
  exitoTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 40, fontWeight: 800, color: 'white', marginBottom: 8,
  },
  exitoSub:  { fontSize: 16, color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow', sans-serif", marginBottom: 16 },
  exitoNota: { fontSize: 13, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif", marginBottom: 24 },
  btnVolver: {
    background: '#FF6B1A', color: 'white', border: 'none',
    borderRadius: 12, padding: '13px 28px', cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800,
  },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 20, padding: 28,
    width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 6,
  },
  modalTitulo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 20, fontWeight: 800, color: '#EF4444',
  },
  modalSub: {
    fontSize: 12, color: '#6B7280', fontFamily: "'Barlow', sans-serif", marginBottom: 20,
  },
  modalLabel: {
    fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 10, fontFamily: "'Barlow', sans-serif",
  },
  btnCerrar: {
    background: '#F4F6FA', border: 'none', borderRadius: 8,
    width: 32, height: 32, fontSize: 14, color: '#6B7280',
    cursor: 'pointer', flexShrink: 0,
  },
  btnCancelarModal: {
    padding: '12px', background: 'white', border: '1.5px solid #E8ECF4',
    borderRadius: 12, fontSize: 14, fontWeight: 700, color: '#6B7280',
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
  },
  btnConfirmarCancelar: {
    padding: '12px', background: '#EF4444', border: 'none', borderRadius: 12,
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: 14, fontWeight: 800, color: 'white',
  },
};
